/**
 * Target counts per weak slot for "View N targets" (PV10, SE-8337).
 *
 * Current list: one slot-filtered Pitch read over the weak slots, counted by
 * effective slot (saved slot, else the slot calculated from the primary
 * position — R04's "assigned, else natural"), paged until complete.
 *
 * All watchlists: the distinct-targets read per slot, filtered by the
 * natural-position ids of the slot's group. Across lists the read knows
 * natural positions only; a slot assigned in another list is not visible
 * here (recorded on the ticket as the R04 divergence).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import useSlotNaturalPositionIds from "@/features/scouting/hooks/useSlotNaturalPositionIds";
import { appendPitchPage } from "@/features/scouting/utils/appendPitchPage";
import {
  type TacticalSlot,
  useScoutingGetWatchlistPitchQuery,
  useScoutingListAllTargetsQuery,
} from "@/generated/graphql";

/** Where "View N targets" counts from. */
export type WeakSlotTargetPool = "list" | "all-watchlists";

/** Backend-enforced maximum page size for Pitch connections. */
const GROUP_PAGE_SIZE = 100;

/** Inputs for the counts. */
export interface UseWeakSlotTargetCountsOptions {
  readonly watchlistId: string;
  /** Weak slots in display order (at most three). */
  readonly slots: readonly TacticalSlot[];
  readonly pool: WeakSlotTargetPool;
  readonly hasAuth: boolean;
  /**
   * Pitch revision; an advance (a saved assignment, order or formation
   * write) re-reads the list counts, which live in their own cache entry.
   */
  readonly revision?: number;
}

/** Counts per slot, null while unknown. */
export interface WeakSlotTargetCounts {
  readonly countFor: (slot: TacticalSlot) => number | null;
  readonly loading: boolean;
}

/** Counts keyed by slot code. */
type CountsBySlot = Readonly<Partial<Record<TacticalSlot, number | null>>>;

/**
 * Reads one slot's distinct total across the user's watchlists.
 * @param slot - Weak slot, or undefined when there is none at this index.
 * @param ids - Natural-position ids of the slot's group.
 * @param skip - Whether the pool is not all-watchlists.
 * @returns Total, or null while unknown.
 */
const useSlotTotal = (
  slot: TacticalSlot | undefined,
  ids: readonly string[],
  skip: boolean
): number | null => {
  const { data } = useScoutingListAllTargetsQuery({
    variables: { first: 1, filterByPositionIds: [...ids] },
    skip: skip || slot === undefined || ids.length === 0,
  });
  return skip || slot === undefined
    ? null
    : (data?.listAllScoutingTargets.totalCount ?? null);
};

/**
 * Counts the targets available per weak slot for the selected pool.
 * @param options - Watchlist, weak slots, pool and session state.
 * @param options.watchlistId - Open watchlist identity.
 * @param options.slots - Weak slots in display order.
 * @param options.pool - Pool the counts come from.
 * @param options.hasAuth - Whether a session exists.
 * @param options.revision - Pitch revision; an advance re-reads the list counts.
 * @returns Count resolver and load state.
 */
const useWeakSlotTargetCounts = ({
  watchlistId,
  slots,
  pool,
  hasAuth,
  revision,
}: UseWeakSlotTargetCountsOptions): WeakSlotTargetCounts => {
  const listMode = pool === "list";
  const skipList = !hasAuth || !listMode || slots.length === 0;
  const { data, fetchMore, loading, refetch } =
    useScoutingGetWatchlistPitchQuery({
      variables: {
        id: watchlistId,
        first: GROUP_PAGE_SIZE,
        filter: { tacticalSlots: [...slots] },
      },
      skip: skipList,
      notifyOnNetworkStatusChange: true,
      // Outside the normalized cache, like the preference-order controller:
      // `WatchlistPitch.members` is ONE field per list, so a cached
      // slot-filtered page REPLACES the page the field renders (the SE-8337
      // regression: every assignment outside the three weak slots vanished).
      fetchPolicy: "no-cache",
    });
  const pitch = data?.getWatchlistPitch;
  const pageInfo = pitch?.members.pageInfo;
  const queryKey = `${watchlistId}|${slots.join(",")}`;
  const requested = useRef<string | null>(null);
  const seenRevision = useRef(revision);
  const listComplete = pitch ? !pitch.members.pageInfo.hasNextPage : false;
  const listCounts = useMemo<CountsBySlot>(
    () =>
      listComplete
        ? (pitch?.members.edges ?? []).reduce<CountsBySlot>((counts, edge) => {
            const slot = edge.node.effectiveSlot;
            return slot
              ? { ...counts, [slot]: (counts[slot] ?? 0) + 1 }
              : counts;
          }, {})
        : {},
    [listComplete, pitch]
  );

  const allMode = pool === "all-watchlists";
  const skipAll = !hasAuth || !allMode || slots.length === 0;
  const { idsFor } = useSlotNaturalPositionIds(skipAll);
  const [first, second, third] = slots;
  const firstTotal = useSlotTotal(first, first ? idsFor(first) : [], skipAll);
  const secondTotal = useSlotTotal(
    second,
    second ? idsFor(second) : [],
    skipAll
  );
  const thirdTotal = useSlotTotal(third, third ? idsFor(third) : [], skipAll);
  const allCounts = useMemo<CountsBySlot>(
    () =>
      Object.fromEntries(
        [
          [first, firstTotal],
          [second, secondTotal],
          [third, thirdTotal],
        ].filter(([slot]) => slot !== undefined)
      ) as CountsBySlot,
    [first, firstTotal, second, secondTotal, third, thirdTotal]
  );

  const countFor = useCallback(
    (slot: TacticalSlot): number | null => {
      if (listMode) {
        if (skipList || !listComplete) return null;
        return listCounts[slot] ?? 0;
      }
      return allCounts[slot] ?? null;
    },
    [allCounts, listComplete, listCounts, listMode, skipList]
  );
  const allLoading =
    allMode && !skipAll && slots.some(slot => allCounts[slot] === null);
  const isLoading = listMode
    ? !skipList && (loading || !listComplete)
    : allLoading;

  // A new slot set starts paging from scratch, in the same commit.
  useLayoutEffect(() => {
    requested.current = null;
  }, [queryKey]);
  // A saved write advanced the pitch: the slot-filtered read is its own cache
  // entry, so re-read it. The first revision seen is the one already loaded.
  // While the list read is skipped the revision stays pending, so resuming
  // list mode after a write still re-reads instead of serving the stale entry.
  useEffect(() => {
    if (skipList || seenRevision.current === revision) return;
    seenRevision.current = revision;
    void refetch().catch(() => undefined);
  }, [refetch, revision, skipList]);
  useEffect(() => {
    const after = pageInfo?.endCursor;
    if (skipList || !pageInfo?.hasNextPage || !after) return;
    const token = `${queryKey}|${after}`;
    if (requested.current === token) return;
    requested.current = token;
    const isCurrent = () => requested.current === token;
    void fetchMore({
      variables: { after },
      // A page completing after the slot set changed belongs to the query
      // that was replaced: keep it out of the current one.
      updateQuery: (previous, { fetchMoreResult }) =>
        isCurrent() ? appendPitchPage(previous, fetchMoreResult) : previous,
    }).catch(() => {
      if (isCurrent()) requested.current = null;
    });
  }, [fetchMore, pageInfo, queryKey, skipList]);

  return { countFor, loading: isLoading };
};

export default useWeakSlotTargetCounts;
